'use client';

/**
 * PlaceActionBar — the one thing the bottom of the screen is for.
 *
 * WHAT IT REPLACES, and why all three had to go together:
 *
 *   · PlaceRail (Order / Menu / Book / You) — a four-tab app rail on a screen
 *     that is not an app. "Menu" was the page the guest was already standing
 *     on, and on a browse-only branch the rail reduced to [Menu][You]: one tab
 *     pointing at the current page and one pointing at an empty account. That
 *     is furniture that teaches a guest the product has places to go, then
 *     fails to take them anywhere.
 *   · MiniCartBar — the real cart, rendered by the ordering island *on top of*
 *     the rail, so the single most important control on the screen arrived as
 *     an overlap.
 *   · PublicMenuActionBar — a floating Cart/Discover/Orders pill, also over the
 *     rail, also carrying a cart.
 *
 * So: one bar, one job at a time, driven by `resolvePlaceAction`. When the guest
 * has no job — reading a menu, empty cart — it renders NOTHING and the food gets
 * the whole screen. Nothing here appears, disappears or moves for any reason
 * except the guest's own situation changing.
 *
 * The utility destinations the rail used to hold (You, Search, Book) did not
 * vanish; they moved to the header, where a single-screen page keeps its
 * secondary controls, and into the branch sheet. See PlaceHeader.
 */

import Link from 'next/link';
import { ArrowRight } from 'lucide-react';

import { cn } from '@/components/ui/utils';
import { zClass } from '@/lib/ui/z-layers';
import type { PlaceActionModel } from '@/lib/customer/place-action';

export function PlaceActionBar({
  model,
  basePath,
  onOpenCart,
  onOpenOrders,
  onChooseFulfilment,
  className,
  chow = false,
}: {
  model: PlaceActionModel;
  basePath: string;
  onOpenCart?: () => void;
  /** Opens the guest's orders sheet — where "Track" and "Pay now" lead, because
   *  the routes they used to name (`/m/<slug>/order`, `/pay`) do not exist. */
  onOpenOrders?: () => void;
  onChooseFulfilment?: (key: string) => void;
  className?: string;
  /** Paint the bar as Chow's 54px accent pill instead of the V1 floating card. */
  chow?: boolean;
}) {
  if (model.kind === 'idle') return null;

  return (
    <div
      className={cn(
        'fixed inset-x-0 bottom-0 w-full',
        zClass('shellChrome'),
        'font-[family-name:var(--font-place-control)]',
        // The bar floats clear of the content rather than clamping it: a full
        // bleed band reads as a second page footer, a floating card reads as a
        // control. Centred on the same measure as the menu so it lines up with
        // the dish rows above it instead of spanning a 1400px desktop.
        'px-3 pb-3 pt-2',
        chow && 'chow-place-action px-4 pb-[calc(16px+env(safe-area-inset-bottom))] pt-2',
        'pointer-events-none',
        className,
      )}
      style={chow ? undefined : { paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
    >
      <div className="pointer-events-auto mx-auto w-full [max-width:var(--place-measure,100%)]">
        {model.kind === 'fulfilment' ? (
          <FulfilmentPrompt model={model} onChoose={onChooseFulfilment} chow={chow} />
        ) : model.kind === 'cart' ? (
          <PrimaryBar
            lead={model.lead}
            detail={model.detail}
            cta={model.cta}
            onClick={onOpenCart}
            chow={chow}
          />
        ) : model.kind === 'invite' ? (
          <PrimaryBar lead={model.lead} detail={null} cta={model.cta} href={model.href} chow={chow} />
        ) : (
          // live + pay: the guest is waiting on us, so the bar states the fact
          // first and what to do about it second. `pay` always reads as urgent.
          //
          // Chow follows the app's order journey: this action opens the full
          // order tracker, where status and payment live together. V1 keeps its
          // in-place orders sheet when the island has registered one.
          <PrimaryBar
            lead={model.lead}
            detail={model.detail}
            cta={model.cta}
            onClick={chow && model.href ? undefined : onOpenOrders}
            href={chow && model.href ? model.href : onOpenOrders ? undefined : model.href}
            urgent={model.kind === 'pay' || model.urgent}
            chow={chow}
          />
        )}
      </div>
    </div>
  );
}

/**
 * The bar's ordinary shape: what you have on the left, what happens next on the
 * right, one tap target across the whole thing.
 *
 * ONE TAP TARGET, deliberately. The old mini-cart put the summary and the
 * button on separate hit areas, so tapping the words "3 items" — the obvious
 * thing to tap — did nothing.
 */
function PrimaryBar({
  lead,
  detail,
  cta,
  href,
  onClick,
  urgent = false,
  chow = false,
}: {
  lead: string | null;
  detail: string | null;
  cta: string;
  href?: string;
  onClick?: () => void;
  urgent?: boolean;
  chow?: boolean;
}) {
  const inner = (
    <>
      <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left">
        {lead ? (
          <span className="truncate text-sm font-semibold leading-tight">{lead}</span>
        ) : null}
        {detail ? (
          <span
            className={cn(
              'truncate text-xs leading-tight opacity-80',
              /\d/.test(detail) && 'font-[family-name:var(--font-place-data)] tabular-nums',
            )}
          >
            {detail}
          </span>
        ) : null}
      </span>

      <span className="flex shrink-0 items-center gap-1 text-sm font-semibold">
        {cta}
        <ArrowRight aria-hidden="true" className="size-4" />
      </span>
    </>
  );

  const shape = cn(
    'flex w-full items-center gap-3',
    chow
      ? 'min-h-[54px] rounded-[24px] px-4 py-3 shadow-none'
      : 'rounded-2xl px-4 py-3.5 min-h-[3.25rem] shadow-[0_10px_30px_rgba(0,0,0,0.22)]',
    'transition-transform duration-150 active:scale-[0.99]',
    'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]',
    chow
      ? 'bg-[hsl(var(--primary))] text-[var(--primary-foreground)]'
      : 'bg-[var(--color-accent)] text-[var(--color-accent-text)]',
    // Urgency is carried by a ring rather than a second colour: the accent is
    // already the merchant's loudest colour, and a red bar on a merchant whose
    // brand IS red says nothing at all.
    urgent && !chow && 'ring-2 ring-offset-2 ring-[var(--color-accent)] ring-offset-[var(--color-bg-base)]',
  );

  if (href) {
    return (
      <Link href={href} className={shape}>
        {inner}
      </Link>
    );
  }

  return (
    <button type="button" onClick={onClick} className={shape}>
      {inner}
    </button>
  );
}

/**
 * "How do you want this?" — asked once, here, at the moment the guest first puts
 * something in the cart.
 *
 * IT IS NOT ASKED ON ARRIVAL. The old shell opened with a modal listing
 * Browse / Dine in / Pickup / Delivery over a menu the guest had not yet seen,
 * which is a logistics question put to someone who has not decided they want
 * anything. Worse, the default answer was a mode called "Browse", which is not
 * a way of getting food — it is the absence of one — and a guest who stayed in
 * it found every dish ending at "Scan the QR code at your table to order this".
 *
 * Here the question has a reason to exist (there are items in the cart), every
 * option leads somewhere real, and it is the only thing on screen asking.
 */
function FulfilmentPrompt({
  model,
  onChoose,
  chow = false,
}: {
  model: Extract<PlaceActionModel, { kind: 'fulfilment' }>;
  onChoose?: (key: string) => void;
  chow?: boolean;
}) {
  return (
    <div
      className={cn(
        'w-full rounded-2xl px-4 py-3.5',
        chow
          ? 'border border-[hsl(var(--chow-rule))] bg-[hsl(var(--chow-paper))] text-[hsl(var(--chow-ink))] shadow-none'
          : 'border border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] shadow-[0_10px_30px_rgba(0,0,0,0.22)]',
      )}
    >
      <p className="text-sm font-semibold leading-tight">{model.question}</p>

      <div
        role="group"
        // Explicit class names, not `grid-cols-${n}`: Tailwind scans source
        // text, so an interpolated class is never generated and the grid
        // silently collapses to one column.
        className={cn(
          'mt-2.5 grid gap-2',
          model.options.length === 1 ? 'grid-cols-1' : 'grid-cols-2',
        )}
      >
        {model.options.map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => onChoose?.(option.key)}
            className={cn(
              'flex min-h-[3rem] flex-col items-start justify-center gap-0.5 rounded-xl px-3 py-2 text-left',
              'border border-[var(--color-border-subtle)] bg-[var(--color-bg-sunken)]',
              'transition-colors active:scale-[0.99]',
              'hover:border-[var(--color-accent)]',
              'outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-accent)]',
            )}
          >
            <span className="truncate text-sm font-semibold leading-tight">{option.label}</span>
            {option.hint ? (
              <span className="truncate text-[11px] leading-tight text-[var(--color-text-secondary)]">
                {option.hint}
              </span>
            ) : null}
          </button>
        ))}
      </div>
    </div>
  );
}
